<script setup lang="ts">
import {
  useManufacturerQuery,
  useManufacturerFiltersQuery,
} from '~~/app/composables/queries/useStoreApi'
import { useAmazzingFilterPanels } from '~~/app/composables/queries/useAmazzingFilterPanels'
import { useAnalytics } from '~~/app/composables/useAnalytics'

const route = useRoute()
const analytics = useAnalytics()

// URL format: /marka/{id}-{brand-slug}  e.g. /marka/1184-dazy
const idSlug = computed(() => String(route.params.id_slug))

const manufacturerId = computed(() => {
  const match = idSlug.value.match(/^(\d+)-/)
  return match?.[1] ? parseInt(match[1], 10) : null
})

if (!manufacturerId.value) {
  throw createError({ statusCode: 404, statusMessage: 'Strona nie istnieje' })
}

const primaryListingQuery = computed(() => ({
  ...route.query,
  currentUrl: route.fullPath,
  filters_scope: 'primary',
  primary_filters_limit: 10,
}))

const {
  data: productsData,
  status,
  isFetching,
} = useManufacturerQuery(computed(() => String(manufacturerId.value)), primaryListingQuery)

const {
  data: filtersData,
  isLoading: filtersLoading,
  isFetching: filtersFetching,
} = useManufacturerFiltersQuery(
  computed(() => String(manufacturerId.value)),
  primaryListingQuery,
  { ssr: true },
)

const manufacturer = computed(() => productsData.value?.manufacturer ?? filtersData.value?.manufacturer)
const productListing = computed(() => productsData.value?.listing ?? null)
const filtersListing = computed(() => filtersData.value?.listing ?? null)
const panels = useAmazzingFilterPanels({
  currentUrl: computed(() => filtersListing.value?.currentUrl ?? route.fullPath),
  filtersMeta: computed(() => filtersListing.value?.filtersMeta),
})
const fullListingQuery = computed(() => ({
  ...route.query,
  currentUrl: route.fullPath,
  filters_scope: 'all',
  primary_filters_limit: 10,
}))
const fullListingState = useManufacturerFiltersQuery(
  computed(() => String(manufacturerId.value)),
  fullListingQuery,
  { enabled: computed(() => panels.showAllFilters.value && panels.canExpandFilters.value) },
)
const visibleListing = computed(() => {
  if (panels.showAllFilters.value && fullListingState.data.value?.listing) {
    return fullListingState.data.value.listing
  }

  return filtersListing.value
})
const products = computed(() => productsData.value?.products ?? [])
const pagination = computed(() => productListing.value?.pagination)
const sortOptions = computed(
  () =>
    productListing.value?.sorting?.options
    ?? filtersListing.value?.sorting?.options
    ?? [],
)
const currentSort = computed(
  () =>
    productListing.value?.sorting?.currentKey
    ?? filtersListing.value?.sorting?.currentKey
    ?? '',
)
// Optimistic local copy — bez tego native <select> mignie do starej wartości
// na czas refetchu (currentSort dochodzi z TanStack Query po round-tripie).
const selectedSort = ref(currentSort.value)
watch(
  currentSort,
  (v) => {
    if (v) selectedSort.value = v
  },
  { immediate: true },
)
function onSortChange(key: string) {
  selectedSort.value = key
  navigateTo({ query: { ...route.query, order: key, page: undefined } })
}
const canExpandFilters = computed(() => panels.canExpandFilters.value && !panels.showAllFilters.value)
const canCollapseFilters = computed(() => panels.showAllFilters.value && Boolean(fullListingState.data.value?.listing))
const isExpandingFilters = computed(() => panels.showAllFilters.value && fullListingState.isFetching.value)
const expandFiltersError = computed(() => panels.showAllFilters.value && fullListingState.error.value
  ? 'Nie udało się załadować dodatkowych filtrów.'
  : null)

const breadcrumbs = computed(() => [
  { label: 'Strona główna', href: '/' },
  { label: 'Marki', href: '/marki' },
  { label: manufacturer.value?.name ?? '' },
])

// Getter w setupie, nie watchEffect — inaczej SSR renderuje pusty <title>
// (patrz useStorefrontSeo).
useStorefrontSeo(() => manufacturer.value
  ? {
      title: manufacturer.value.name,
      description: manufacturer.value.seoDescription,
    }
  : null)

const _brandItemListId = computed(() => `brand_${manufacturerId.value ?? ''}`)
const _brandItemListName = computed(() => manufacturer.value?.name ?? 'Marka')
let _lastTrackedListKey = ''
watch(
  [products, _brandItemListId],
  ([list, id]) => {
    if (!import.meta.client || !list.length) return
    const key = `${id}|${list.length}|${list[0]?.id ?? ''}`
    if (key === _lastTrackedListKey) return
    _lastTrackedListKey = key
    analytics.trackViewItemList(list, {
      itemListId: id,
      itemListName: _brandItemListName.value,
    })
  },
  { immediate: true },
)
</script>

<template>
  <div class="brand-page">
    <CategoryBreadcrumbs :items="breadcrumbs" />

    <section class="brand-hero">
      <div class="brand-hero__content container">
        <h1 class="brand-hero__title">{{ manufacturer?.name ?? '&nbsp;' }}</h1>
        <p v-if="manufacturer?.shortDescription" class="brand-hero__desc muted">
          {{ manufacturer.shortDescription }}
        </p>
      </div>
    </section>

    <div class="listing container">
      <ProductFilters
        :listing="visibleListing"
        :is-initial-loading="filtersLoading"
        :is-updating="filtersFetching && !filtersLoading"
        mobile-modal-filters
        :can-expand-filters="canExpandFilters"
        :can-collapse-filters="canCollapseFilters"
        :is-expanding-filters="isExpandingFilters"
        :expand-filters-error="expandFiltersError"
        @expand-filters="panels.expandAllFilters()"
        @collapse-filters="panels.collapseAllFilters()"
      />

      <div class="listing__right">
        <div class="listing__toolbar">
          <p v-if="status !== 'pending'" class="listing__count">
            Wyświetlam {{ pagination?.from ?? 0 }}–{{ pagination?.to ?? 0 }} produktów z {{ pagination?.totalItems ?? 0 }}
          </p>
          <div v-if="sortOptions.length" class="listing__sort">
            <label class="listing__sort-label" for="marka-sort-select">
              Sortuj po:
            </label>
            <select
              id="marka-sort-select"
              class="listing__sort-select"
              :value="selectedSort"
              aria-label="Sortuj produkty marki"
              @change="onSortChange(($event.target as HTMLSelectElement).value)"
            >
              <option
                v-for="option in sortOptions"
                :key="option.key"
                :value="option.key"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="listing__grid" :class="{ 'is-fetching': isFetching && status !== 'pending' }">
          <ProductCard
            v-for="(product, index) in products"
            :key="product.id"
            :product="product"
            :eager="index < 8"
            :item-list-id="_brandItemListId"
            :item-list-name="_brandItemListName"
            :index="index"
          />
        </div>
        <ProductPagination
          v-if="status !== 'pending' && pagination && pagination.totalPages > 1"
          :current-page="pagination.currentPage"
          :total-pages="pagination.totalPages"
          :total-items="pagination.totalItems"
          :previous-page-url="pagination.previousPageUrl"
          :next-page-url="pagination.nextPageUrl"
          :pages="pagination.pages"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.brand-page {
  display: flex;
  flex-direction: column;
}

.brand-hero {
  background: var(--color-bg-secondary, #f5f5f5);
  padding: 48px 0 40px;
}

.brand-hero__title {
  margin: 0;
  font-size: 2.25rem;
  font-weight: var(--font-weight-light);
  text-transform: uppercase;
  letter-spacing: 0.4rem;
  line-height: 1;
}

.brand-hero__desc {
  margin: 12px 0 0;
  font-size: 0.9375rem;
  line-height: 1.7;
  max-width: 600px;
}

.listing {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 40px;
  padding-top: 40px;
  padding-bottom: 40px;
  align-items: start;
}

.listing__right {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.listing__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  min-height: 36px;
}

.listing__sort {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.listing__sort-label {
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  white-space: nowrap;
}

.listing__sort-select {
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--color-text);
  background: var(--color-white);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 6px 28px 6px 10px;
  cursor: pointer;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none' stroke='%23555' stroke-width='1.5'><path d='M1 1l4 4 4-4'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  min-height: 36px;
}

.listing__sort-select:focus {
  border-color: var(--color-primary);
}

.listing__count {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  line-height: 1.4;
}

.listing__grid {
  display: grid;
  /* Domyślnie 2 kolumny (do 1599px); 3 kolumny dopiero od 1600px (niżej). */
  grid-template-columns: repeat(2, 1fr);
  gap: 4px;
}

/* Ultra-wide: 3 produkty w rzędzie od 1600px. */
@media (min-width: 1600px) {
  .listing__grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Katalog (proles): 4 kafle w rzędzie na desktopie — jak w [...slug].vue
   (03.11.2026). minmax(0, …) jak w pozostałych listingach — samo 1fr pozwala
   długiej nazwie poszerzyć kolumnę. */
@media (min-width: 1200px) {
  [data-brand="proles"] .listing__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.listing__grid.is-fetching {
  opacity: 0.4;
  pointer-events: none;
  transition: opacity 0.2s;
}

@media (max-width: 1200px) {
  .listing {
    grid-template-columns: 240px 1fr;
    gap: 24px;
  }
}

@media (max-width: 960px) {
  .listing {
    grid-template-columns: 1fr;
  }
}
</style>
